import {gsap, TimelineLite, TweenMax, TweenLite, TimelineMax} from "gsap";
import {BitmapText, Sprite} from "pixi.js";
import {TheCrypt} from "../TheCrypt";
import {TheCryptHelper} from "../utils/TheCryptHelper";
import {TheCryptGameConfig} from "../TheCryptGameConfig";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import Spine = PIXI.spine.Spine;
import {TheCryptFontConfig} from "../configs/TheCryptFontConfig";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {TheCryptSoundManager} from "../sound/TheCryptSoundManager";
import {ArrayUtils} from "../utils/ArrayUtils";
import {TheCryptAnimationConfig} from "../configs/TheCryptAnimationConfig";
import {TheCryptSoundConfig} from "../configs/TheCryptSoundConfig";

export interface ISymbolData {
    symName: string,
    reelSize: number,
    symSize: number,
    id: number
    reelId: number
    hasHighlight?: boolean
    isWild?: boolean
    M1Multiplier?: number
}

export enum TheCryptSymbolStates {
    NORMAL,
    BLUR,
    WIN,
    NO_WIN,
    LANDING,
    EXPAND,
    NUDGE
}

export class TheCryptSymbol extends PIXI.Container {

    private _id: number = -1;
    private _reelId: number = -1;
    private _isRemoved: boolean = false;
    private _name: string;
    private _textureName: string;
    private _currentStateSprite: Sprite;
    private _spineName: string;
    protected _spine: Spine | null;
    private _symHeight: number;
    private _symWidth: number;
    private _reelSize: number;
    private _symSize: number;
    private _state: TheCryptSymbolStates;
    private _isWild: boolean = false;
    private _highlighter: PIXI.Sprite;
    private _M1Multiplier: BitmapText;
    protected _wildMultiplier: BitmapText;
    protected _wildMultiplierEffect: BitmapText;

    private _isSpecialSymbol: boolean;
    private _isNormalSymbol: boolean;
    private _hasHighlight: boolean;
    private _symbolData: ISymbolData;

    private isSpecialSymbol(): boolean {
        const symbolName: string = this._name;
        return (symbolName === "W" || symbolName.indexOf("FW") > -1 || symbolName === "M");
    }

    private isNormalSymbol(): boolean {
        const symbolName: string = this._name;
        if (this._isSpecialSymbol) {
            return false;
        }
        return (symbolName[0] === "L" || symbolName[0] === "M");
    }

    private getTextureName(symSize: number): string {
        return "";
    }

    private getHighLighterTextureName(symSize: number): string {
        if (this._name === "FW") {
            return "potential_xway_wins/potential_win_stacked";
        }

        return `potential_xway_wins/potential_win_${symSize}`;
    }

    private isMSymbol(): boolean {
        return this.symName != "M" && this.symName[0] === "M";
    }

    private getSpineName(symSize: number): string {
        if (this._isNormalSymbol) {
            return this.isMSymbol() ? `SymMSize${symSize}` : `SymLSize${symSize}`;
        } else if (this._name === "W" || this._name === "S") {
            return `${this._name}_${symSize}`;
        } else if (this._name === "FW" || this._name === "M") {
            return `${this._name}`;
        }
        return "";
    }

    protected getSymHeight(): number {
        return (TheCryptGameConfig.reelHeight / this._reelSize);
    }

    protected addSpine(): void {
        if (this._spine) {
            return;
        }
        this._spine = new Spine(TheCrypt.assetLoader.getSpineAsset(this._spineName));
        this.addChild(this._spine);
        if (this._isNormalSymbol) {
            this._spine.skeleton.setSkinByName(this._textureName);
        }
    }

    public playLandingAnimation(): TimelineLite {
        return new TimelineLite().add(new SpineTween(this._spine!, 0, "land"));
    }

    public getStateAnimation(state: TheCryptSymbolStates = TheCryptSymbolStates.NORMAL, duration: number = 0): TimelineLite {
        if (TheCryptHelper.isInvisibleSym(this.symName)) {
            return gsap.timeline();
        }

        if (this._state === state) {
            return gsap.timeline();
        }

        const tl: TimelineLite = new TimelineLite();
        const isBlur: boolean = (state === TheCryptSymbolStates.BLUR),
            textureName: string = this.getTextureName(this._symSize);
        if (this._spineName.length > 0) {
            this.addSpine();
            if (isBlur) {
                tl.add(() => {
                    this._spine?.state.setAnimation(0, "spin", false)
                }, 0);
            } else if (state === TheCryptSymbolStates.NUDGE) {
                tl.add(() => {
                    this._spine?.state.setAnimation(1, "nudge", false);
                }, 0);
            } else if (state === TheCryptSymbolStates.NORMAL) {
                tl.add(() => {
                    this._spine?.state.setAnimation(0, "base", this._name === "S" ? true : false);
                }, 0);
            } else if (state === TheCryptSymbolStates.LANDING) {
                if (["W", "M"].indexOf(this._name) > -1) {
                    tl.add(this.playLandingAnimation());
                } else if(this._name !== "S"){
                    tl.add(() => {
                        this._spine?.state.setAnimation(0, "base", false)
                    });
                }
            } else {
                tl.add(() => {
                    this._currentStateSprite.visible = false;
                }, 0);
            }
        } else {
            tl.add(() => {
                this._currentStateSprite.texture = TheCrypt.assetLoader.getTextures(`${textureName}${isBlur ? "_spin" : ""}`)[0];
                this._currentStateSprite.visible = true;
                this.addChild(this._currentStateSprite);
            }, 0);
        }
        this._state = state;
        return tl;
    }

    public init(initState: TheCryptSymbolStates): TheCryptSymbol {
        if (TheCryptHelper.isInvisibleSym(this.symName)) {
            return this;
        }
        this.getStateAnimation(initState);
        return this;
    }

    constructor(symbolData: ISymbolData, state: TheCryptSymbolStates = TheCryptSymbolStates.NORMAL) {
        super();
        let symbolName: string = symbolData.symName;
        if (symbolName.indexOf("_W") > 0) {
            symbolName = symbolName[0] + symbolName[1];
            this._isWild = true;
        }
        this.name = symbolName + symbolData.symSize.toString();
        this._isRemoved = false;

        this._name = symbolName;
        if (symbolName == undefined) {
            debugger;
        }
        this._textureName = symbolName;
        this._isSpecialSymbol = this.isSpecialSymbol();
        this._isNormalSymbol = this.isNormalSymbol();

        this._currentStateSprite = new PIXI.Sprite();
        this._hasHighlight = symbolData.hasHighlight ? symbolData.hasHighlight : false;
        this._currentStateSprite.name = "currentStateSprite";
        this._currentStateSprite.anchor.set(0.5, 0.5);
        this._currentStateSprite.position.set(0, 0);
        this._highlighter = new PIXI.Sprite(TheCrypt.assetLoader.getTextures(this.getHighLighterTextureName(symbolData.symSize))[0]);
        this._highlighter.anchor.set(0.5, 0.5);
        this._highlighter.position.set(0, 0);
        this._highlighter.visible = false;
        this._spineName = this.getSpineName(symbolData.symSize);

        this._id = symbolData.id;
        this._reelId = symbolData.reelId;
        this.reelSize = symbolData.reelSize;
        this._symWidth = TheCryptGameConfig.symbolWidth;
        this._symHeight = this.getSymHeight();
        this.init(state);
        if (symbolName === "W" || symbolName === "FW") {
            this.addWildMultiplier();
        }

        if (symbolData.M1Multiplier && symbolData.M1Multiplier > 1) {
            this.patchMidSymWildOrMultiplier(TheCrypt.model.m1CurrentMultiplier, false);
        }

        if (symbolData.isWild || this._isWild) {
            this.patchMidSymWildOrMultiplier(TheCrypt.model.data.m1CurrentMultiplier, false);
        }
        this._symbolData = symbolData;
    }


    public fallOut(): void {

    }

    public playInitialWin(winType: string): TimelineMax | SpineTween {
        this._isRemoved = true;
        winType += (winType.length > 0 ? "_" : "");
        if (this._isNormalSymbol) {
            this.parent.removeChild(this);
            if(this._M1Multiplier && !TheCrypt.model.isNormal){
                TheCrypt.realArea.allReels[this._reelId].reelView.scatterWinView.addChild(this);
            }else {
                TheCrypt.realArea.allReels[this._reelId].reelView.initialWinSymbolView.addChild(this);
            }
            return new SpineTween(this._spine!, 0, `${winType}win_toggle`);
        }
        return this.playWinHighlighter();
    }

    public collect(): TimelineLite {
        if (TheCryptHelper.isInvisibleSym(this._name)) {
            return new TimelineLite();
        }
        const isWild: boolean = (this._isWild || this._name.indexOf("FW") > -1);
        return new TimelineLite().add(new SpineTween(this._spine!, isWild ? 2 : 1, `collect${this._name === "M1" ? "_m1" : ""}`));
    }

    public changeHeight(symExpandDuration: number = 0, reelSize: number, scale: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();

        if (this._spineName === "M") {
            return tl.add([
                new SpineTween(this._spine!, 0, `expand_${reelSize - 4 + 1}${TheCrypt.model.hasNoAvalancheWin ? "_fast" : ""}`)
            ], symExpandDuration - 0.2).add(() => {
                this.remove();
            }, 38 * 0.033);
        }
        if (this._spineName.length > 0) {
            tl.add(TweenMax.to(this._spine!.scale, symExpandDuration, {y: scale}));
        } else if (this._isSpecialSymbol) {
            tl.add(TweenMax.to(this._currentStateSprite!.scale, symExpandDuration, {y: scale}));
        }
        return tl;
    }

    public breakOnNudge(stepDuration: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this._isNormalSymbol) {
            tl.add(new SpineTween(this._spine!, 1, "nudge_break"), 0.1);
            tl.add(TweenLite.to(this._spine!, stepDuration, {alpha: 1}));
            if (this._M1Multiplier) {
                tl.add(() => {
                    this._M1Multiplier.visible = false
                });
            }
        } else {
            throw Error(`Nudge on special symbol: ${this._name}`);
        }
        return tl;
    }

    public explodeOnNudge(winType: string, isWin: boolean = false): TimelineLite {
        winType += (winType.length > 0 ? "_" : "");
        const tl: TimelineLite = new TimelineLite()
        if (this._isNormalSymbol) {
            tl.add(new SpineTween(this._spine!, this._isWild ? 2 : 1, `${winType}win_break`), 0);
        }
        if (this._wildMultiplier) {
            tl.add(TweenLite.to(this._wildMultiplier, 0.2, {alpha: 0}), 0).add(() => {
                this._wildMultiplier.visible = false
            });
        }
        if (this._M1Multiplier) {
            tl.add(TweenLite.to(this._M1Multiplier, 0.2, {alpha: 0}), 0).add(() => {
                this._M1Multiplier.visible = false
            });
        }

        if (isWin) {
            tl.add(() => {
                this.remove()
            });
        }

        return tl;
    }

    public explode(winType: string): TimelineLite {
        return this.explodeOnNudge(winType, true);
    }

    private patchM1Multiplier(multiplier: number, withAnimation: boolean = true): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            duration: number = withAnimation ? TheCryptGameConfig.SYMBOL_HIGHLIGHT_DURATION : 0;

        if (!this._M1Multiplier) {
            this._M1Multiplier = new BitmapText(`X${multiplier}`, TheCryptFontConfig.M1_MULTIPLIER_STYLE);
        } else {
            this._M1Multiplier.text = `X${multiplier}`;
        }

        this._M1Multiplier.position.set(this._symWidth * 0.5 - this._M1Multiplier.width - 6, -this._symHeight * 0.5);
        this._M1Multiplier.alpha = 0;
        this._M1Multiplier.letterSpacing = 2;
        this.addChild(this._M1Multiplier);
        if (withAnimation) {
            tl.add(new SpineTween(this._spine!, 1, "turn_mult"), 0);
        }
        if (multiplier > 1) {
            tl.add(TweenLite.to(this._M1Multiplier, duration, {alpha: 1}), 0);
            tl.add(() => {
                this._spine!.state.setAnimation(1, `m1_mult_${multiplier > 5 ? 5 : multiplier}`, true);
            });
        }

        return tl;
    }

    public patchMidSymWildOrMultiplier(multiplier: number, withAnimation: boolean = true): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this._name === "M1") {
            return this.patchM1Multiplier(multiplier, withAnimation);
        } else {
            this._isWild = true;
        }

        if (withAnimation) {
            tl.add(new SpineTween(this._spine!, 1, "turn_wild"));
        }

        tl.add(() => {
            this._spine!.alpha = 1;
            this._spine!.state.setAnimation(1, "wild_state", true)
        });
        return tl;
    }

    public playWinHighlighter(delay: number = 1.5): TimelineLite {
        this._highlighter.alpha = 0;
        return new TimelineLite().add(
            () => {
                this._highlighter.alpha = 1;
                this._highlighter.visible = true;
                this.addChild(this._highlighter);
            }).add(TweenLite.to(this._highlighter, 0.3, {alpha: 0, delay:delay-0.3}));
    }

    public remove(): TheCryptSymbol {
        if (this._wildMultiplier) {
            this._wildMultiplier.destroy();
        }

        if (this._M1Multiplier) {
            this._M1Multiplier.destroy();
        }

        if (this._wildMultiplierEffect) {
            this._wildMultiplierEffect.destroy();
        }

        if (this._spine) {
            this._spine.destroy();
            this._spine = null;
        }

        if (this.parent) {
            this.parent.removeChild(this);
        }
        this.destroy({children: true});
        this._isRemoved = true;
        return this;
    }

    private multiplierAnimation(multiplier: number, stepDuration: number, delay: number = 0, hasNudge: boolean = true): TimelineLite {
        this.addChild(this._wildMultiplier);
        this.addChild(this._wildMultiplierEffect);
        this._wildMultiplierEffect.scale.set(1);
        return new TimelineLite().add(() => {
            if (!this._isRemoved) {
                this._wildMultiplier.text = `X${multiplier}`;
                this._wildMultiplierEffect.text = `X${multiplier}`;
                if (hasNudge) {
                    this._spine?.state.setAnimation(1, "nudge", false);
                }
                TweenLite.fromTo(this._wildMultiplierEffect, stepDuration, {alpha: 1}, {
                    alpha: 0,
                    delay: stepDuration * 0.5
                });
                TweenLite.to(this._wildMultiplier, 0, {alpha: 1});
                TweenLite.fromTo(this._wildMultiplierEffect.scale, stepDuration, {
                    x: 1,
                    y: 1
                }, {
                    x: 1.5 + Math.min(0.5, multiplier * 0.1),
                    y: 1.5 + Math.min(0.5, multiplier * 0.1),
                    ease: "sine.out"
                });
            }
        }, delay);
    }

    public showNudgeMultiplier(stepDuration: number, noWin: boolean = false, hasNudge: boolean = true): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            y: number = this.position.y;
        this._wildMultiplierEffect = new BitmapText(`X1`, TheCryptFontConfig.NUDGE_WILD_MULTIPLIER_STYLE);
        this._wildMultiplierEffect.anchor = new PIXI.Point(0.5, 0.5);
        //this._wildMultiplierEffect.blendMode = PIXI.BLEND_MODES.ADD;
        this._wildMultiplierEffect.position.set(-6, 40);
        this._wildMultiplierEffect.letterSpacing = 6;

        this._wildMultiplierEffect.alpha = 0;
        this._wildMultiplier.alpha = 0;
        this._wildMultiplier.visible = true;
        this._wildMultiplier.text = "X1";

        if (noWin) {
            return tl;
        } else {
            tl.add(TweenLite.to(this, 0.1, {y: y - 15}));
            tl.add(TweenLite.to(this, 0.2, {y: y}));
        }

        stepDuration -= 0.3;
        tl.add(this.multiplierAnimation(1, stepDuration, 0, hasNudge));
        return tl;
    }

    public upgradeWildMultiplier(multiplier: number, stepDuration: number, delay: number, hasNudge: boolean = true): TimelineLite {
        return this.multiplierAnimation(multiplier, stepDuration, delay + stepDuration, hasNudge);
    }

    public addWildMultiplier(): void {
        this._wildMultiplier = new BitmapText(`X`, TheCryptFontConfig.NUDGE_WILD_MULTIPLIER_STYLE);
        this._wildMultiplier.anchor = new PIXI.Point(0.5, 0.5);
        this._wildMultiplier.position.set(-6, 40);
        this._wildMultiplier.letterSpacing = 6;
        this._wildMultiplier.visible = false;
    }

    public getShakeAnimation(duration: number = 1.715, maxWin: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (this._name === "Y") {
            return tl;
        }
        const wiggleX = maxWin ? TheCryptAnimationConfig.capWinAnticipationWiggleX : TheCryptAnimationConfig.bigWinAnticipationWiggle,
            wiggleY = maxWin ? TheCryptAnimationConfig.capWinAnticipationWiggleY : TheCryptAnimationConfig.bigWinAnticipationWiggle2;
        const wiggles = ArrayUtils.shuffleArray([wiggleX, wiggleY]);
        const x = `${Math.random() > 0.5 ? "-" : "+"}=2`;
        const y = `${Math.random() > 0.5 ? "-" : "+"}=1`
        const rotPostFix: string = (this._name === "FW" ? "0.01" : "0.03"),
            rot = Math.random() > 0.5 ? "-=" + rotPostFix : "+=" + rotPostFix;

        tl.add(gsap.to(this.position, {
            duration: duration,
            x: x,
            ease: wiggles[0]
        }));
        tl.add(gsap.to(this.position, {
            duration: duration,
            y: y,
            ease: wiggles[1]
        }), "<");
        if (this._spine) {
            tl.add(gsap.to(this._spine, {
                duration: duration,
                rotation: rot,
                ease: wiggles[1]
            }), "<");
        } else {
            tl.add(gsap.to(this._currentStateSprite, {
                duration: duration,
                rotation: rot,
                ease: wiggles[1]
            }), "<");
        }

        return tl.reverse();
    }

    public setSymbolState(symbolData: ISymbolData): void {
        if (!symbolData) {
            return;
        }
        if (symbolData.M1Multiplier && symbolData.M1Multiplier > 1) {
            this.patchMidSymWildOrMultiplier(TheCrypt.model.data.m1CurrentMultiplier, false);
        }
        if (symbolData.isWild || this._isWild) {
            this.patchMidSymWildOrMultiplier(TheCrypt.model.data.m1CurrentMultiplier, false);
        }
    }

    public get isRemoved(): boolean {
        return this._isRemoved;
    }

    public set isRemoved(value: boolean) {
        this._isRemoved = value;
    }

    public set wildMultiplier(value: string) {
        this._wildMultiplier.text = value;
    }

    public get symName(): string {
        return this._name;
    }

    public get id(): number {
        return this._id;
    }

    public get reelId(): number {
        return this._reelId;
    }

    public set id(value: number) {
        this._id = value;
    }

    public set reelSize(value: number) {
        this._reelSize = value;
        const symSize: number = value - 6 + 3;
        this._symHeight = TheCryptGameConfig.reelHeight / value;

        if (this._spineName.length > 0) {
            if (this._spine) {
                const oldSpine: Spine = this._spine!,
                    newSpine: Spine = new Spine(TheCrypt.assetLoader.getSpineAsset(this.getSpineName(symSize)));
                if (this._isNormalSymbol) {
                    newSpine.skeleton.setSkinByName(this._name);
                }
                this.addChild(newSpine);
                oldSpine.destroy();
                this._spine = null;
                this._spine = newSpine;
            }
        } else if (this._symSize != symSize && !TheCryptHelper.isInvisibleSym(this._name)) {
            this._currentStateSprite.texture = TheCrypt.assetLoader.getTextures(this.getTextureName(symSize))[0];
        }
        this.setSymbolState(this._symbolData);
        this._symSize = symSize;
    }

    public get symHeight(): number {
        return this._symHeight;
    }

    public get isXWay(): boolean {
        return this._name === "M";
    }
}